import type { IAccountDataResponse } from '@hit-game/shared/types';
import { Check, Copy, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { accountService, type ICryptoDepositInfo } from '../../services/account.service';
import { formatMoney } from '../../utils/format';
import { Dropdown } from '../common/Dropdown';
import { Modal } from '../common/Modal';

/** Coin và mạng chọn sẵn khi mở — đổi được, nhưng 99% lần nạp là USDT qua BEP20. */
const DEFAULT_CURRENCY = 'USDT';
const DEFAULT_NETWORK = 'BEP20';

/** Đưa mục mặc định lên đầu, giữ nguyên thứ tự của phần còn lại. */
function defaultFirst<T>(items: readonly T[], isDefault: (item: T) => boolean): T[] {
  return [...items.filter(isDefault), ...items.filter((item) => !isDefault(item))];
}

/**
 * Mạng nên chọn sẵn cho một coin: BEP20 nếu coin đó có, không thì mạng duy nhất,
 * không thì để người dùng tự chọn. Chỉ chọn sẵn thứ TỒN TẠI trong danh sách đọc từ
 * paygate — mặc định không được ép một mạng nhà cái đã tắt.
 */
function preferredNetwork(coin: ICryptoDepositInfo | undefined): string {
  if (!coin) return '';
  if (coin.networks.some((item) => item.network === DEFAULT_NETWORK)) return DEFAULT_NETWORK;
  return coin.networks.length === 1 ? (coin.networks[0]?.network ?? '') : '';
}

/** Coin nên chọn sẵn: USDT nếu có, không thì coin duy nhất, không thì để trống. */
function preferredCoin(data: readonly ICryptoDepositInfo[]): ICryptoDepositInfo | undefined {
  return (
    data.find((item) => item.currency === DEFAULT_CURRENCY) ??
    (data.length === 1 ? data[0] : undefined)
  );
}

interface DepositModalProps {
  account: IAccountDataResponse | null;
  onClose: () => void;
}

/**
 * Địa chỉ + QR nạp tiền điện tử.
 *
 * Danh sách coin và mạng lưới đọc LIVE từ paygate mỗi lần mở, KHÔNG hard-code và
 * không cache: paygate bật/tắt coin và đổi chain bất cứ lúc nào, chuyển tiền vào
 * mạng đã tắt là mất luôn.
 */
export function DepositModal({ account, onClose }: DepositModalProps) {
  const [options, setOptions] = useState<ICryptoDepositInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('');
  const [network, setNetwork] = useState('');
  const [copied, copy] = useCopyToClipboard();

  const accountId = account ? String(account._id) : null;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await accountService.cryptoDeposit(accountId);
      setOptions(data);

      // Chọn sẵn USDT + BEP20 (nếu paygate đang mở); chỉ một lựa chọn thì chọn luôn.
      const coin = preferredCoin(data);
      setCurrency(coin?.currency ?? '');
      setNetwork(preferredNetwork(coin));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedCoin = useMemo(
    () => options.find((item) => item.currency === currency),
    [options, currency],
  );
  const selectedNetwork = selectedCoin?.networks.find((item) => item.network === network);

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      maxWidth={440}
      title={
        <span className="flex items-center gap-2">
          Nạp tiền điện tử
          <span className="text-ink-dim text-[12px] font-normal">{account?.username}</span>
        </span>
      }
    >
      {loading ? (
        <div className="text-ink-dim flex items-center justify-center gap-2 py-10 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Đang đọc cấu hình từ nhà cái...
        </div>
      ) : options.length === 0 ? (
        <div className="text-ink-dim py-10 text-center text-[13px]">
          Nhà cái đang không mở kênh nạp tiền điện tử nào.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-ink-dim mb-2 block text-[10px] font-bold uppercase tracking-wider">
              Loại tiền
            </label>
            <Dropdown
              value={currency}
              onChange={(value) => {
                setCurrency(value);
                // Mạng lưới thuộc về coin — đổi coin mà giữ mạng cũ là gửi vào chain sai.
                setNetwork(preferredNetwork(options.find((item) => item.currency === value)));
              }}
              searchable={false}
              placeholder="-- Chọn coin --"
              options={defaultFirst(options, (item) => item.currency === DEFAULT_CURRENCY).map(
                (item) => ({ value: item.currency, label: item.currency }),
              )}
            />
            {selectedCoin && (
              <p className="text-ink-faint mt-1 text-[10px]">
                Tỉ giá nạp: {formatMoney(selectedCoin.price)} VND / 1 {selectedCoin.currency}
              </p>
            )}
          </div>

          <div>
            <label className="text-ink-dim mb-2 block text-[10px] font-bold uppercase tracking-wider">
              Mạng lưới
            </label>
            <Dropdown
              value={network}
              onChange={setNetwork}
              disabled={!selectedCoin}
              searchable={false}
              placeholder="-- Chọn mạng lưới --"
              options={defaultFirst(
                selectedCoin?.networks ?? [],
                (item) => item.network === DEFAULT_NETWORK,
              ).map((item) => ({
                value: item.network,
                label: item.network,
              }))}
            />
          </div>

          {selectedNetwork && (
            <div className="border-line bg-canvas space-y-3 rounded-lg border p-4 text-center">
              {selectedNetwork.qr && (
                <img
                  src={selectedNetwork.qr}
                  alt={`QR nạp ${selectedNetwork.network}`}
                  className="mx-auto h-40 w-40 rounded-lg bg-white p-2"
                />
              )}

              <div>
                <p className="text-ink-dim mb-1 text-[10px] font-bold uppercase tracking-wider">
                  Địa chỉ ví
                </p>
                <p className="text-ink break-all font-mono text-[12px]">
                  {selectedNetwork.address}
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  copy(selectedNetwork.address);
                }}
                className="border-line bg-panel text-ink hover:bg-panel-hover mx-auto flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[12px] font-bold transition-colors"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Đã chép' : 'Chép địa chỉ'}
              </button>

              <p className="text-warning text-[10px]">
                Chỉ gửi {selectedCoin?.currency} qua mạng {selectedNetwork.network}. Gửi sai mạng là
                mất tiền, nhà cái không hoàn.
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
